import { useMemo } from 'react';
import type { Point, Rect } from '../../doc/geometry';
import type { ConnectorCap, ConnectorItem, ConnectorProps, Item } from '../../doc/schema';
import {
  BLOCK_HEAD_HALF,
  BLOCK_HEAD_LEN,
  attachBoxOf,
  blockBorder,
  capSize,
  connectorGeometry,
  LABEL_LINE,
  LABEL_PAD_X,
  LABEL_PAD_Y,
  labelInk,
  labelPoint,
  mapLooseEnds,
  pathD,
  pathLength,
  pathTip,
  trimPath,
  type BoxLookup,
  type ConnectorGeometry,
  type ConnectorPath,
} from '../../doc/connector';
import { ConnectorLabel } from '../connector/ConnectorLabel';
import { useBoard, usePane } from '../context';

/**
 * One thing to paint: a path (world coordinates), filled and/or stroked. The board draws these
 * as SVG paths, the exporter and the colour picker on a canvas, so all three agree.
 */
export interface DrawOp {
  d: string;
  fill?: string;
  stroke?: string;
  width?: number;
  cap?: CanvasLineCap;
  join?: CanvasLineJoin;
  dash?: number[];
}

/** What a connector paints: a line with its end caps, or a block arrow's band and heads. */
export function connectorOps(p: ConnectorProps, geo: ConnectorGeometry): DrawOp[] {
  return p.kind === 'block' ? blockOps(p, geo) : lineOps(p, geo);
}

const f = (q: Point) => `${q.x.toFixed(2)},${q.y.toFixed(2)}`;
const polygon = (pts: Point[]) => `M${pts.map(f).join('L')}Z`;

function lineOps(p: ConnectorProps, geo: ConnectorGeometry): DrawOp[] {
  const w = p.width;
  // a cap never takes more than about a third of a short line
  const size = Math.min(capSize(w), pathLength(geo.path) * 0.35);
  const start = capOps(p.startCap, geo.path, 'start', size, p);
  const end = capOps(p.endCap, geo.path, 'end', size, p);
  const body = trimPath(geo.path, start.trim, end.trim);
  const dash = p.dash === 'dashed' ? [w * 3, w * 2.5] : p.dash === 'dotted' ? [0.01, w * 2.2] : undefined;
  return [{ d: pathD(body), stroke: p.stroke, width: w, cap: p.dash === 'dashed' ? 'butt' : 'round', join: 'round', dash }, ...start.ops, ...end.ops];
}

/** An end cap at the tip of the path, and how much of the line it hides (the line stops short of it). */
function capOps(cap: ConnectorCap, path: ConnectorPath, which: 'start' | 'end', size: number, p: ConnectorProps): { trim: number; ops: DrawOp[] } {
  if (cap === 'none' || size <= 0) return { trim: 0, ops: [] };
  const w = p.width;
  const { tip, dir } = pathTip(path, which, size);
  const n = { x: -dir.y, y: dir.x };
  const at = (back: number, side: number): Point => ({ x: tip.x - dir.x * back + n.x * side, y: tip.y - dir.y * back + n.y * side });
  switch (cap) {
    case 'arrow': {
      // an open V; its mitred point lands on the tip
      const v = w * 1.1;
      const half = size * 0.5;
      return {
        trim: v,
        ops: [{ d: `M${f(at(v + size, half))}L${f(at(v, 0))}L${f(at(v + size, -half))}`, stroke: p.stroke, width: w, cap: 'round', join: 'miter' }],
      };
    }
    case 'triangle':
      return { trim: size * 0.75, ops: [{ d: polygon([tip, at(size, size * 0.42), at(size, -size * 0.42)]), fill: p.stroke }] };
    case 'circle': {
      const r = size * 0.32;
      const c = at(r, 0);
      const d = `M${(c.x + r).toFixed(2)},${c.y.toFixed(2)}A${r},${r} 0 1 0 ${(c.x - r).toFixed(2)},${c.y.toFixed(2)}A${r},${r} 0 1 0 ${(c.x + r).toFixed(2)},${c.y.toFixed(2)}Z`;
      return { trim: r, ops: [{ d, fill: p.stroke }] };
    }
    case 'diamond':
      return { trim: size * 0.5, ops: [{ d: polygon([tip, at(size / 2, size * 0.32), at(size, 0), at(size / 2, -size * 0.32)]), fill: p.stroke }] };
  }
}

/**
 * A block arrow: a band of the body width along the path with a head at each end that has one.
 * The outline is the band and the heads drawn a little wider in the outline colour first, then
 * again in the fill colour, so it runs round their union with clean joins on any route.
 */
function blockOps(p: ConnectorProps, geo: ConnectorGeometry): DrawOp[] {
  const w = p.width;
  const b = blockBorder(p);
  const total = pathLength(geo.path);
  const startHead = p.startCap !== 'none';
  const endHead = p.endCap !== 'none';
  const heads = (startHead ? 1 : 0) + (endHead ? 1 : 0);
  // heads shrink (keeping their shape) on an arrow too short for them
  const k = heads ? Math.min(1, (total * (heads === 2 ? 0.45 : 0.7)) / (w * BLOCK_HEAD_LEN)) : 1;
  const hl = w * BLOCK_HEAD_LEN * k;
  const hh = w * BLOCK_HEAD_HALF * k;
  const head = (which: 'start' | 'end') => {
    const { tip, dir } = pathTip(geo.path, which, hl);
    const n = { x: -dir.y, y: dir.x };
    const base = { x: tip.x - dir.x * hl, y: tip.y - dir.y * hl };
    return polygon([tip, { x: base.x + n.x * hh, y: base.y + n.y * hh }, { x: base.x - n.x * hh, y: base.y - n.y * hh }]);
  };
  const polys = [...(startHead ? [head('start')] : []), ...(endHead ? [head('end')] : [])];
  // square caps carry the band half its width past the cut: a tail stops at its end, the band runs into a head
  const body = pathD(trimPath(geo.path, startHead ? hl : w / 2, endHead ? hl : w / 2));
  const join: CanvasLineJoin = p.route === 'curved' ? 'round' : 'miter';
  const ops: DrawOp[] = [];
  if (b > 0) {
    ops.push({ d: body, stroke: p.stroke, width: w + b * 2, cap: 'square', join });
    for (const d of polys) ops.push({ d, fill: p.stroke, stroke: p.stroke, width: b * 2, join: 'miter' });
  }
  ops.push({ d: body, stroke: p.fill, width: w, cap: 'square', join });
  for (const d of polys) ops.push({ d, fill: p.fill });
  return ops;
}

/**
 * A connector as it is right now: its props with any handle edit in progress, loose ends carried
 * by its own drag, and its ends resolved against its items' live drag geometry, so it follows
 * them while they are still moving.
 */
export function useLiveConnector(item: ConnectorItem): { props: ConnectorProps; geo: ConnectorGeometry } {
  const board = useBoard();
  const draft = usePane((s) => s.connectorDraft[item.id]);
  const own = usePane((s) => s.transient[item.id]);
  const startId = (draft?.start ?? item.props.start).item;
  const endId = (draft?.end ?? item.props.end).item;
  const ts = usePane((s) => (startId ? s.transient[startId] : undefined));
  const te = usePane((s) => (endId ? s.transient[endId] : undefined));
  return useMemo(() => {
    if (!draft && !own && !ts && !te) return { props: item.props, geo: board.index.connectorGeometry(item) };
    const boxOf: BoxLookup = (id) => {
      const it = board.index.get(id);
      const t = id === startId ? ts : id === endId ? te : undefined;
      return attachBoxOf(it && t ? ({ ...it, ...t } as Item) : it);
    };
    let props = draft ? { ...item.props, ...draft } : item.props;
    if (own) props = mapLooseEnds(props, item, { ...item, ...own } as Rect, boxOf);
    return { props, geo: connectorGeometry(props, boxOf) };
  }, [board, item, draft, own, ts, te, startId, endId]);
}

/**
 * The connector on the board. The box only places it (the path may run outside it while things
 * are dragged); only the painted line and a wider invisible band along it take the pointer, so
 * the empty corners of its box never hide what is underneath.
 */
export function ConnectorBody({ item, origin, selected }: { item: ConnectorItem; origin: Point; selected: boolean }) {
  const { props, geo } = useLiveConnector(item);
  const zoom = usePane((s) => s.settledZoom);
  const ops = useMemo(() => connectorOps(props, geo), [props, geo]);
  const full = useMemo(() => pathD(geo.path), [geo]);
  const hit = Math.max(props.kind === 'block' ? props.width : props.width + 8, 14 / zoom);
  return (
    <>
      <svg className={`connector-svg connector-${props.kind}`} width={item.w} height={item.h} overflow="visible" aria-hidden>
        <g transform={`translate(${-origin.x} ${-origin.y})`}>
          <OpPaths ops={ops} className="connector-paint" />
          {selected && <path className="connector-selected" d={full} strokeWidth={1.5 / zoom} />}
          <path className="connector-hit" d={full} strokeWidth={hit} />
        </g>
      </svg>
      <ConnectorLabel item={item} props={props} geo={geo} origin={origin} selected={selected} />
    </>
  );
}

export function OpPaths({ ops, className }: { ops: DrawOp[]; className?: string }) {
  return ops.map((op, i) => (
    <path
      key={i}
      className={className}
      d={op.d}
      fill={op.fill ?? 'none'}
      stroke={op.stroke && op.stroke !== 'none' ? op.stroke : 'none'}
      strokeWidth={op.width}
      strokeLinecap={op.cap}
      strokeLinejoin={op.join}
      strokeMiterlimit={4}
      strokeDasharray={op.dash?.join(' ')}
    />
  ));
}

/** What a label painted on a canvas takes from the board: the canvas colour behind it and the board font. */
export interface LabelLook {
  canvas: string;
  font: string;
}

/** The label look of the board shown in a pane (or any element inside one): its CSS variables. */
export function labelLookOf(el: Element | null, canvas?: string | null): LabelLook {
  const cs = el ? getComputedStyle(el) : null;
  return {
    canvas: canvas || cs?.getPropertyValue('--canvas-bg').trim() || '#ffffff',
    font: cs?.getPropertyValue('--font-ui').trim() || 'monospace',
  };
}

/** Paints a connector onto a canvas whose origin is the top left of the connector's box. */
export function paintConnector(ctx: CanvasRenderingContext2D, item: ConnectorItem, geo: ConnectorGeometry, look: LabelLook): void {
  ctx.save();
  ctx.translate(-item.x, -item.y);
  for (const op of connectorOps(item.props, geo)) {
    const path = new Path2D(op.d);
    if (op.fill && op.fill !== 'none') {
      ctx.fillStyle = op.fill;
      ctx.fill(path);
    }
    if (op.stroke && op.stroke !== 'none' && op.width) {
      ctx.strokeStyle = op.stroke;
      ctx.lineWidth = op.width;
      ctx.lineCap = op.cap ?? 'butt';
      ctx.lineJoin = op.join ?? 'miter';
      ctx.miterLimit = 4;
      ctx.setLineDash(op.dash ?? []);
      ctx.stroke(path);
    }
  }
  paintLabel(ctx, item.props, geo, look);
  ctx.restore();
}

/**
 * The label as the board's .connector-label draws it: its box in the canvas colour (a block
 * arrow's: in its fill, continuing the band), the text centred.
 */
function paintLabel(ctx: CanvasRenderingContext2D, p: ConnectorProps, geo: ConnectorGeometry, look: LabelLook): void {
  if (!p.label) return;
  const s = p.labelSize;
  const c = labelPoint(p, geo);
  ctx.font = `${s}px ${look.font}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  const m = ctx.measureText(p.label);
  const w = m.width + s * LABEL_PAD_X * 2;
  const h = s * (LABEL_LINE + LABEL_PAD_Y * 2);
  ctx.fillStyle = p.kind === 'block' ? p.fill : look.canvas;
  ctx.fillRect(c.x - w / 2, c.y - h / 2, w, h);
  // CSS centres the font's ascent + descent in the line; the baseline sits that far below the middle
  const ascent = m.fontBoundingBoxAscent;
  const descent = m.fontBoundingBoxDescent;
  const baseline = Number.isFinite(ascent) && Number.isFinite(descent) ? c.y + (ascent - descent) / 2 : c.y + s * 0.35;
  ctx.fillStyle = labelInk(p);
  ctx.fillText(p.label, c.x, baseline);
}
